import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { ROLE_LABELS } from "../lib/constants";
import { toast } from "sonner";
import { UserPlus, Activity } from "lucide-react";

const ROLES = ["it_admin", "pmo", "executive", "it_executive", "engineer"];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [ws, setWs] = useState([]);
  const [health, setHealth] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "engineer", workstream_id: "", title: "" });

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/workstreams").then((r) => setWs(r.data)).catch(() => {});
    api.get("/system-health").then((r) => setHealth(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const wsCode = (id) => ws.find((w) => w.id === id)?.code || "—";

  const createUser = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    try {
      await api.post("/users", { ...form, workstream_id: form.workstream_id || null });
      toast.success("User created"); setShowForm(false);
      setForm({ name: "", email: "", role: "engineer", workstream_id: "", title: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const updateUser = async (id, changes) => {
    try { await api.patch(`/users/${id}`, changes); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733]">User &amp; Role Management</h1>
          <p className="text-[#5A687C] mt-1">Invite users, assign roles and workstreams, and deactivate accounts.</p>
        </div>
        <button data-testid="new-user-btn" onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-1.5 bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full px-5 py-2.5 font-semibold"><UserPlus className="h-4 w-4" /> New user</button>
      </div>

      {health && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[["Total users", health.users_total], ["Active", health.users_active], ["Logged in", health.users_logged_in], ["Audit entries", health.data_volume.audit_log]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-[18px] border border-[#DCE2EA] p-5"><div className="text-3xl font-extrabold text-[#BA0C2F]">{v}</div><div className="text-xs text-[#5A687C] mt-1 font-medium">{l}</div></div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 grid md:grid-cols-2 gap-3">
          <input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
          <input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
          <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm">
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <select value={form.workstream_id} onChange={(e) => setForm({ ...form, workstream_id: e.target.value })} className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm">
            <option value="">No workstream</option>
            {ws.map((w) => <option key={w.id} value={w.id}>{w.code} — {w.title}</option>)}
          </select>
          <button data-testid="create-user-btn" onClick={createUser} className="bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full py-2.5 font-semibold">Create user (default password: Carle2026!)</button>
        </div>
      )}

      <div className="bg-white rounded-[18px] border border-[#DCE2EA] overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="bg-[#F4F6FA] text-[#5A687C] text-xs font-bold uppercase tracking-wider">
            <th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th>
            <th className="text-left px-4 py-3">Workstream</th><th className="text-left px-4 py-3">Last login</th><th className="text-left px-4 py-3">Active</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#DCE2EA]/60 hover:bg-[#F4F6FA]/60">
                <td className="px-4 py-3 font-semibold text-[#1F2733]">{u.name}<div className="text-[11px] text-[#98A2AF] font-normal">{u.title}</div></td>
                <td className="px-4 py-3 text-[#5A687C]">{u.email}</td>
                <td className="px-4 py-3">
                  <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => updateUser(u.id, { role: e.target.value })} className="px-2.5 py-1 rounded-full border border-[#DCE2EA] text-xs font-semibold bg-white">
                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select value={u.workstream_id || ""} onChange={(e) => updateUser(u.id, { workstream_id: e.target.value || null })} className="px-2.5 py-1 rounded-full border border-[#DCE2EA] text-xs bg-white">
                    <option value="">—</option>{ws.map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-[#98A2AF] text-xs">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "Never"}</td>
                <td className="px-4 py-3">
                  <button data-testid={`toggle-active-${u.id}`} onClick={() => updateUser(u.id, { active: !u.active })}
                    className={`text-xs font-semibold px-3 py-1 rounded-full ${u.active ? "bg-[#ECF8F1] text-[#1A5633]" : "bg-[#FDF2F4] text-[#8A0822]"}`}>{u.active ? "Active" : "Inactive"}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
